/**
 * SecurePayments — badge strip that renders ONLY payment methods
 * confirmed active by `GET /api/checkout/available-methods`.
 *
 * Implementation contract:
 *   • Each badge is rendered as `<img>` pointing at a static SVG served
 *     from `/assets/payment-icons/*.svg`. The SVGs carry their own
 *     `width`/`height`/`viewBox`, so the browser always has a sane
 *     intrinsic size to scale from and the icons never collapse to 0×0
 *     inside inline-flex containers.
 *   • No `dangerouslySetInnerHTML`, no CSS `fill`/`stroke` colour
 *     mutations that could hide brand marks.
 *   • Empty API response → component returns null (no phantom badges).
 *
 * Variants:
 *   `full`    — footer strip with the "Secure checkout powered by Stripe"
 *               caption, larger icons.
 *   `compact` — inline row for near-checkout placements (cart / track pages).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Map: backend method code → { label, file }. The `file` MUST exist under
// /public/assets/payment-icons/. Adding a new method later = drop the SVG
// + register it here.
const ICONS = {
  card_visa:       { label: "Visa",              file: "visa.svg" },
  card_mastercard: { label: "Mastercard",         file: "mastercard.svg" },
  card_amex:       { label: "American Express",   file: "amex.svg" },
  card_discover:   { label: "Discover",           file: "discover.svg" },
  apple_pay:       { label: "Apple Pay",          file: "apple-pay.svg" },
  google_pay:      { label: "Google Pay",         file: "google-pay.svg" },
  link:            { label: "Link by Stripe",     file: "link.svg" },
};

// Module-level cache — same response for every mount within a session.
// Cleared on hard reload; that's fine because Stripe method availability
// changes on the order of hours/days, not per navigation.
let _cachedPayload = null;

export default function SecurePayments({ variant = "full", className = "" }) {
  const [payload, setPayload] = useState(_cachedPayload);
  useEffect(() => {
    if (_cachedPayload) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/checkout/available-methods");
        if (cancelled) return;
        _cachedPayload = data;
        setPayload(data);
      } catch {
        // Fail closed — hide the badges entirely rather than show wrong ones.
        if (!cancelled) setPayload({ methods: [], stripe_ready: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const methods = (payload?.methods || []).filter((m) => ICONS[m]);
  if (methods.length === 0) return null;

  const isCompact = variant === "compact";
  // Explicit pixel sizes — no CSS shorthand that could evaporate to 0.
  const iconHeightPx = isCompact ? 20 : 26;
  // Rendered aspect ratio locked at ~1.56:1 (matches viewBox 780×500) so
  // Visa, Mastercard, AmEx, Discover, Google Pay, Apple Pay all read as
  // real card-shaped badges instead of squished tiles.
  const iconWidthPx = Math.round(iconHeightPx * 1.56);

  return (
    <div
      data-testid="secure-payments"
      data-variant={variant}
      className={`${isCompact ? "flex items-center gap-2 flex-wrap" : "flex flex-col items-start gap-2"} ${className}`}
    >
      {!isCompact && (
        <div className="font-mono text-[10px] tracking-[0.3em] text-white/40">
          SECURE CHECKOUT POWERED BY STRIPE
        </div>
      )}
      <div className={`flex items-center flex-wrap ${isCompact ? "gap-1.5" : "gap-2"}`} dir="ltr">
        {methods.map((m) => (
          <img
            key={m}
            src={`/assets/payment-icons/${ICONS[m].file}`}
            alt={ICONS[m].label}
            title={ICONS[m].label}
            aria-label={ICONS[m].label}
            width={iconWidthPx}
            height={iconHeightPx}
            loading="lazy"
            decoding="async"
            draggable={false}
            data-testid={`payment-badge-${m}`}
            className="rounded-[4px] bg-white/[0.03] border border-white/10 select-none"
            style={{
              width: `${iconWidthPx}px`,
              height: `${iconHeightPx}px`,
              objectFit: "contain",
              display: "inline-block",
            }}
          />
        ))}
      </div>
    </div>
  );
}
